import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus, Package, Wrench } from 'lucide-react';
import { InventoryItems } from '@/components/inventory/InventoryItems';
import { ProductRecipes } from '@/components/inventory/ProductRecipes';
import { InventoryTransactions } from '@/components/inventory/InventoryTransactions';
import { ProductionRuns } from '@/components/inventory/ProductionRuns';
import { ErrorBoundary } from '@/components/ErrorBoundary';

export default function Inventory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get initial tab from URL or default to 'items'
  const initialTab = searchParams.get('tab') || 'items';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  // Sync tab state with URL on mount and URL changes
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams, activeTab]);

  return (
    <AppLayout currentPage="inventory">
      <div className="container-responsive space-y-6">
        <div className="space-y-2">
          <h1 className="mobile-heading">Inventory Management</h1>
          <p className="mobile-body text-muted-foreground">
            Manage raw materials, finished products, and production
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-3 sm:space-y-4">
          <TabsList className="grid w-full grid-cols-4 h-auto">
            <TabsTrigger value="items" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-2 sm:px-4 sm:py-2.5">
              <Package className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Items</span>
            </TabsTrigger>
            <TabsTrigger value="recipes" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-2 sm:px-4 sm:py-2.5">
              <Wrench className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Recipes</span>
              <span className="xs:hidden">Recipe</span>
            </TabsTrigger>
            <TabsTrigger value="transactions" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-2 sm:px-4 sm:py-2.5">
              <span className="hidden xs:inline">Transactions</span>
              <span className="xs:hidden">Trans</span>
            </TabsTrigger>
            <TabsTrigger value="production" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-2 sm:px-4 sm:py-2.5">
              <span className="hidden xs:inline">Production</span>
              <span className="xs:hidden">Prod</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="items" className="space-y-4">
            <ErrorBoundary>
              <InventoryItems />
            </ErrorBoundary>
          </TabsContent>

          <TabsContent value="recipes" className="space-y-4">
            <ErrorBoundary>
              <ProductRecipes />
            </ErrorBoundary>
          </TabsContent>

          <TabsContent value="transactions" className="space-y-4">
            <ErrorBoundary>
              <InventoryTransactions />
            </ErrorBoundary>
          </TabsContent>

          <TabsContent value="production" className="space-y-4">
            <ErrorBoundary>
              <ProductionRuns />
            </ErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}